import { Modal } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/hooks/useAuth'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import {
  submitSupportRequest,
  type SupportRequestResult,
  type SupportRequestSubject,
} from '../api/supportRequestClient'
import { SUPPORT_PHONE } from '../constants'

type SupportRequestModalProps = {
  isOpen: boolean
  onClose: () => void
}

type SupportSubjectOption = {
  value: SupportRequestSubject
  messageId: string
  defaultMessage: string
}

const SUPPORT_SUBJECT_OPTIONS: SupportSubjectOption[] = [
  {
    value: 'help_with_my_account',
    messageId: 'home.support.form.subject.options.helpWithMyAccount',
    defaultMessage: 'Help with my account',
  },
  {
    value: 'cancel_a_test',
    messageId: 'home.support.form.subject.options.cancelATest',
    defaultMessage: 'Cancel a test',
  },
  {
    value: 'questions_about_consent',
    messageId: 'home.support.form.subject.options.questionsAboutConsent',
    defaultMessage: 'Questions about consent',
  },
  {
    value: 'request_a_copy_of_my_report',
    messageId: 'home.support.form.subject.options.requestCopyOfReport',
    defaultMessage: 'Request a copy of my report',
  },
  {
    value: 'where_are_my_results',
    messageId: 'home.support.form.subject.options.whereAreMyResults',
    defaultMessage: 'Where are my results?',
  },
  {
    value: 'something_else',
    messageId: 'home.support.form.subject.options.somethingElse',
    defaultMessage: 'Something else',
  },
]

const SUPPORT_DESCRIPTION_MAX_LENGTH = 5000

const SupportRequestModal: React.FC<SupportRequestModalProps> = ({ isOpen, onClose }) => {
  const intl = useIntl()
  const { user } = useAuth()
  const apiClient = useFamilyPortalApiClient()
  const [subject, setSubject] = useState<SupportRequestSubject | ''>('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SupportRequestResult | null>(null)

  const isFormValid = Boolean(subject && description.trim())
  const confirmationEmail = user?.email ?? ''

  function formatSupportPhoneDisplay(phone: string): string {
    const digits = phone.replace(/\D/g, '')
    const nationalNumber = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits

    if (nationalNumber.length === 10) {
      return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`
    }

    return phone.replace(/^\+/, '')
  }

  function resetAndClose() {
    if (submitting) return
    setSubject('')
    setDescription('')
    setError(null)
    setResult(null)
    onClose()
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmedDescription = description.trim()
    if (!subject || !trimmedDescription || submitting) return

    setSubmitting(true)
    setError(null)
    try {
      const response = await submitSupportRequest(apiClient, {
        subject,
        description: trimmedDescription,
      })
      setResult(response)
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'home', action: 'submit-support-request' } })
      setError(
        intl.formatMessage({
          id: 'home.support.form.error.submit',
          defaultMessage: "We couldn't send your request. Please try again.",
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={intl.formatMessage({ id: 'home.support.form.title', defaultMessage: 'Contact support' })}
      size="lg"
    >
      {result?.submitted ? (
        <div className="flex min-h-[248px] flex-col">
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950"
            role="status"
          >
            <h3 className="mb-2 text-base font-semibold">
              <FormattedMessage id="home.support.form.success.heading" defaultMessage="We got your message" />
            </h3>
            <p>
              <FormattedMessage
                id="home.support.form.success.body"
                defaultMessage="Our team will reply to {email} as soon as possible. If you don't see it, check your spam folder."
                values={{ email: confirmationEmail }}
              />
            </p>
            <p className="mt-2">
              <FormattedMessage
                id="home.support.form.success.profilePrompt"
                defaultMessage="Not the right email? <profileLink>Update it in your profile.</profileLink>"
                values={{
                  profileLink: (chunks) => (
                    <Link to="/profile" className="font-semibold text-[#8E00E8] underline-offset-2 hover:underline">
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </p>
          </div>
          <div className="mt-auto flex justify-end border-t border-[#E5E7EB] pt-4">
            <button
              type="button"
              onClick={resetAndClose}
              className="rounded-full bg-[#8E00E8] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#7600C2] focus:ring-2 focus:ring-[#8E00E8]/25 focus:outline-none"
            >
              <FormattedMessage id="home.support.form.done" defaultMessage="Done" />
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="support-subject" className="mb-2 block text-sm font-medium text-gray-900">
              <FormattedMessage id="home.support.form.subject.label" defaultMessage="What can we help with?" />{' '}
              <span className="text-red-500" aria-hidden="true">
                *
              </span>
            </label>
            <select
              id="support-subject"
              required
              value={subject}
              onChange={(event) => setSubject(event.target.value as SupportRequestSubject | '')}
              className="w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#8E00E8] focus:ring-2 focus:ring-[#8E00E8]/20 focus:outline-none"
            >
              <option value="">
                {intl.formatMessage({
                  id: 'home.support.form.subject.placeholder',
                  defaultMessage: 'Select a topic',
                })}
              </option>
              {SUPPORT_SUBJECT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {intl.formatMessage({ id: option.messageId, defaultMessage: option.defaultMessage })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="support-description" className="mb-2 block text-sm font-medium text-gray-900">
              <FormattedMessage id="home.support.form.description.label" defaultMessage="How can we help?" />{' '}
              <span className="text-red-500" aria-hidden="true">
                *
              </span>
            </label>
            <textarea
              id="support-description"
              required
              rows={7}
              maxLength={SUPPORT_DESCRIPTION_MAX_LENGTH}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={intl.formatMessage({
                id: 'home.support.form.description.placeholder',
                defaultMessage: 'Tell us a little more about what you need.',
              })}
              className="min-h-36 w-full resize-y rounded-xl border border-[#E5E7EB] bg-white px-3 py-3 text-sm text-gray-900 shadow-sm focus:border-[#8E00E8] focus:ring-2 focus:ring-[#8E00E8]/20 focus:outline-none"
            />
          </div>

          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <div className="border-t border-[#E5E7EB] pt-4 text-sm text-gray-600">
            <FormattedMessage id="home.support.form.phonePrompt" defaultMessage="Prefer to call?" />{' '}
            <a
              href={`tel:${SUPPORT_PHONE}`}
              className="font-semibold text-[#8E00E8] underline-offset-2 hover:underline"
            >
              {formatSupportPhoneDisplay(SUPPORT_PHONE)}
            </a>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-[#E5E7EB] pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
              className="rounded-full border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:ring-2 focus:ring-[#8E00E8]/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FormattedMessage id="home.support.form.cancel" defaultMessage="Cancel" />
            </button>
            <button
              type="submit"
              disabled={!isFormValid || submitting}
              aria-busy={submitting}
              className="inline-flex min-w-24 items-center justify-center rounded-full bg-[#8E00E8] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#7600C2] focus:ring-2 focus:ring-[#8E00E8]/25 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
            >
              {submitting
                ? intl.formatMessage({ id: 'home.support.form.sending', defaultMessage: 'Sending' })
                : intl.formatMessage({ id: 'home.support.form.send', defaultMessage: 'Send' })}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

export default SupportRequestModal
